import { Box, ButtonBase, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { ACTIVE_SURFACE_MUTED } from "@/shared/theme";

interface QuickAnalysisWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onContinueQuick: () => void;
  onUseRegular: () => void;
  onEntered?: () => void;
  instantTransition?: boolean;
}

export const QuickAnalysisWarningDialog = ({
  open,
  onClose,
  onContinueQuick,
  onUseRegular,
  onEntered,
  instantTransition = false,
}: QuickAnalysisWarningDialogProps) => {
  const [showInitialRecommendation, setShowInitialRecommendation] = useState(false);
  const [initialRecommendationFading, setInitialRecommendationFading] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowInitialRecommendation(false);
      setInitialRecommendationFading(false);
      return;
    }
    setShowInitialRecommendation(true);
    setInitialRecommendationFading(false);
    const fadeTimer = window.setTimeout(() => setInitialRecommendationFading(true), 1_700);
    const clearTimer = window.setTimeout(() => setShowInitialRecommendation(false), 2_000);
    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(clearTimer);
    };
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      transitionDuration={instantTransition ? 0 : undefined}
      TransitionProps={{ onEntered }}
    >
      <DialogTitle>QUICK LOOK. CLEAR TRADE-OFF.</DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 1.5, sm: 3 } }}>
        <Typography
          sx={{
            fontSize: { xs: "1.65rem", sm: "2rem", md: "3.5rem" },
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 1,
          }}
        >
          Nikola can move fast.
          <br />
          Patterns need proof.
        </Typography>
        <Typography
          sx={{
            mt: { xs: 1, sm: 1.5 },
            color: "text.secondary",
            fontSize: { xs: "0.7rem", sm: "0.82rem" },
            lineHeight: 1.45,
          }}
        >
          Quick catches the loud mistakes. Regular gives Nikola the depth to prove what belongs in Patterns.
        </Typography>

        <Box
          sx={{
            mt: { xs: 2, sm: 3, md: 4 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
            gap: { xs: 0.75, sm: 1.5 },
          }}
        >
          <ButtonBase
            onClick={onContinueQuick}
            aria-label="Keep Quick analysis"
            sx={{
              minWidth: 0,
              textAlign: "left",
              "&:focus-visible > div": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: { xs: 168, sm: 204, md: 228 },
                boxSizing: "border-box",
                p: { xs: 2, sm: 2.5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                bgcolor: "background.paper",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
                transition: "none",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "background.default",
                  borderColor: "primary.main",
                  "& .quick-choice-detail": { color: ACTIVE_SURFACE_MUTED },
                },
              }}
            >
              <Typography
                sx={{
                  alignSelf: "flex-start",
                  textAlign: "left",
                  color: "inherit",
                  fontSize: { xs: "3rem", sm: "3.9rem", md: "4.4rem" },
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                ♞
              </Typography>
              <Box>
                <Typography
                  sx={{
                    color: "inherit",
                    fontWeight: 800,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.12rem" },
                    letterSpacing: "0.04em",
                  }}
                >
                  KEEP QUICK · D12
                </Typography>
                <Typography
                  className="quick-choice-detail"
                  sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}
                >
                  Fast review. Loud mistakes only.
                  <br />
                  Tactical Patterns wait.
                </Typography>
              </Box>
            </Box>
          </ButtonBase>

          <ButtonBase
            onClick={() => {
              onUseRegular();
            }}
            aria-label="Use Regular analysis"
            sx={{
              minWidth: 0,
              textAlign: "left",
              "&:focus-visible > div": { outline: "2px solid", outlineColor: "background.default", outlineOffset: -4 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: { xs: 168, sm: 204, md: 228 },
                boxSizing: "border-box",
                p: { xs: 2, sm: 2.5 },
                position: "relative",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                bgcolor: "background.paper",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
                transition: "none",
                "&:hover": {
                  bgcolor: "primary.main",
                  color: "background.default",
                  borderColor: "primary.main",
                  "& .regular-choice-detail": { color: ACTIVE_SURFACE_MUTED },
                  "& .recommended-hover-hint": {
                    display: showInitialRecommendation ? "none" : "inline-block",
                  },
                },
              }}
            >
              <Typography
                sx={{
                  alignSelf: "flex-start",
                  textAlign: "left",
                  color: "inherit",
                  fontSize: { xs: "3rem", sm: "3.9rem", md: "4.4rem" },
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                ♝
              </Typography>
              {showInitialRecommendation && (
                <Box
                  component={motion.span}
                  data-testid="initial-regular-recommendation"
                  initial={{ opacity: 0, rotateX: -90 }}
                  animate={{ opacity: initialRecommendationFading ? 0 : 1, rotateX: 0 }}
                  transition={{ type: "spring", stiffness: 155, damping: 15, mass: 1.35 }}
                  sx={{
                    position: "absolute",
                    top: { xs: 2, md: 2.5 },
                    right: { xs: 2, md: 2.5 },
                    display: "inline-block",
                    fontSize: "0.58rem",
                    fontWeight: 700,
                    fontFamily: "Arial, sans-serif",
                    letterSpacing: "0.04em",
                    whiteSpace: "nowrap",
                    textTransform: "uppercase",
                    transformOrigin: "50% 0%",
                    transformPerspective: 640,
                  }}
                >
                  Recommended
                </Box>
              )}
              <Box
                className="recommended-hover-hint"
                component="span"
                sx={{
                  display: "none",
                  position: "absolute",
                  top: { xs: 2, md: 2.5 },
                  right: { xs: 2, md: 2.5 },
                  fontSize: "0.58rem",
                  fontWeight: 700,
                  fontFamily: "Arial, sans-serif",
                  letterSpacing: "0.04em",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                }}
              >
                Recommended
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "inherit",
                    fontWeight: 800,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.12rem" },
                    letterSpacing: "0.04em",
                  }}
                >
                  USE REGULAR · D18
                </Typography>
                <Typography
                  className="regular-choice-detail"
                  sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}
                >
                  Nikola checks the line twice. Verified Patterns count.
                </Typography>
              </Box>
            </Box>
          </ButtonBase>

          <ButtonBase
            onClick={onClose}
            aria-label="Close without starting analysis"
            sx={{
              minWidth: 0,
              textAlign: "left",
              "&:focus-visible > div": { outline: "2px solid", outlineColor: "secondary.main", outlineOffset: 2 },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: { xs: 168, sm: 204, md: 228 },
                boxSizing: "border-box",
                p: { xs: 2, sm: 2.5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                bgcolor: "background.paper",
                color: "text.primary",
                border: "1px solid",
                borderColor: "divider",
                transition: "none",
                "&:hover": {
                  bgcolor: "secondary.main",
                  color: "background.default",
                  borderColor: "secondary.main",
                  "& .close-choice-detail": {
                    color: "rgba(10,10,10,0.66)",
                    fontWeight: 700,
                  },
                },
              }}
            >
              <Typography
                sx={{
                  alignSelf: "flex-start",
                  textAlign: "left",
                  color: "inherit",
                  fontSize: { xs: "3rem", sm: "3.9rem", md: "4.4rem" },
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                #
              </Typography>
              <Box>
                <Typography
                  sx={{
                    color: "inherit",
                    fontWeight: 800,
                    fontSize: { xs: "1rem", sm: "1.1rem", md: "1.12rem" },
                    letterSpacing: "0.04em",
                  }}
                >
                  CLOSE
                </Typography>
                <Typography
                  className="close-choice-detail"
                  sx={{ mt: 0.5, color: "text.secondary", fontSize: "0.68rem", lineHeight: 1.4 }}
                >
                  No move made. Nikola keeps the board as it is.
                </Typography>
              </Box>
            </Box>
          </ButtonBase>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
